import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useEndpoints } from "../../../api/ApiContext";
import type { DualControlRequest, DualControlRequestList } from "../../../api/types";
import { useSession } from "../../../auth/AuthProvider";
import { homeFor } from "../../../auth/pages";
import { CommandError } from "../../../components/CommandError";
import { CursorListView } from "../../../components/CursorListView";
import { RejectionDetail } from "../../../components/RejectionDetail";
import { Banner, Button, Card, Dialog, EmptyState, PageHeader, StatusPill } from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { useCursorList } from "../../../lib/useCursorList";
import { useIntentKey } from "../../../lib/useIntentKey";
import { ACTION_TYPE_TITLE, blockerText, DC_STATUS, humanize, OrgTabs, SINGLE_USER_FALLBACK_TEXT } from "./orgCopy";

// Organization → Pending approvals (G-09; UX-DESIGN.md §4.3, the shared
// dual-control + cooling-off pattern). GET /v1/dual-control-requests
// (org.go:447-482), approve (RequireRecentMFA, org.go:65) and cancel
// (org.go:66). Every action type is listed here — payout-destination
// changes, role grants and ownership transfers — so this replaces the
// payout-destination-only approval the portal used to point at (G-42).
// can_approve / approval_blocker / can_cancel are computed by the server
// for the viewer (list_dual_control_requests, 0062:1742-1746); the
// buttons follow them, and a refusal is still explained if they're stale.

type Filter = "open" | "closed" | "all";

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
];

export function ApprovalsPage() {
  const ep = useEndpoints();
  const session = useSession();
  const home = homeFor(session.partyType);
  const [filter, setFilter] = useState<Filter>("open");
  const list = useCursorList<DualControlRequestList, DualControlRequest>(
    ["approvals", filter],
    (cursor) => ep.approvals.list(filter, cursor),
    (p) => p.requests,
  );

  return (
    <div className="stack-lg">
      <PageHeader
        title="Pending approvals"
        description="Changes that need a second member: payout-destination changes, privileged role grants and ownership transfers. Either member can cancel until it takes effect."
      />
      <OrgTabs home={home} />
      <div className="row" role="group" aria-label="Show requests">
        {FILTERS.map((f) => (
          <Button key={f.value} size="sm" variant={filter === f.value ? "primary" : "secondary"} aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>
      <CursorListView
        list={list}
        loadingLabel="Loading requests"
        empty={
          <EmptyState icon="check" title={filter === "open" ? "Nothing waiting for approval" : "No requests"}>
            Requests appear here when a member asks for a change that needs a second approver.
          </EmptyState>
        }
      >
        {(rows) => (
          <div className="stack">
            {rows.map((r) => (
              <RequestCard key={r.request_id} r={r} home={home} />
            ))}
          </div>
        )}
      </CursorListView>
    </div>
  );
}

function RequestCard({ r, home }: { r: DualControlRequest; home: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const approveIntent = useIntentKey();
  const cancelIntent = useIntentKey();
  const [confirm, setConfirm] = useState<"approve" | "cancel" | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["approvals"] });
    void qc.invalidateQueries({ queryKey: ["org"] });
  };
  const approve = useMutation({
    mutationFn: () => ep.approvals.approve(r.request_id, { idempotencyKey: approveIntent.keyFor(r.request_id) }),
    onSuccess: () => {
      approveIntent.reset();
      setConfirm(null);
      refresh();
    },
    onError: () => setConfirm(null),
  });
  const cancel = useMutation({
    mutationFn: () => ep.approvals.cancel(r.request_id, { idempotencyKey: cancelIntent.keyFor(r.request_id) }),
    onSuccess: () => {
      cancelIntent.reset();
      setConfirm(null);
      refresh();
    },
    onError: () => setConfirm(null),
  });
  const status = DC_STATUS[r.status];
  const open = r.status === "pending" || r.status === "approved";
  const title = ACTION_TYPE_TITLE[r.action_type];

  return (
    <Card level={3} title={title} subtitle={r.summary.text} actions={<StatusPill tone={status.tone}>{status.label}</StatusPill>}>
      <div className="stack">
        <dl className="definition-list">
          <dt>Requested by</dt>
          <dd>
            {r.requested_by?.email ?? "A former member"} on {formatLocal(r.requested_at)}
          </dd>
          <dt>{open ? "Takes effect" : "Earliest effect was"}</dt>
          <dd>no earlier than {formatLocal(r.effective_at)}</dd>
          {r.approved_at ? (
            <>
              <dt>Approved by</dt>
              <dd>
                {r.approved_by?.email ?? "A former member"} on {formatLocal(r.approved_at)}
              </dd>
            </>
          ) : null}
          {r.canceled_at ? (
            <>
              <dt>Canceled by</dt>
              <dd>
                {r.canceled_by?.email ?? "VADS"} on {formatLocal(r.canceled_at)}
              </dd>
            </>
          ) : null}
          {r.executed_at ? (
            <>
              <dt>Took effect</dt>
              <dd>{formatLocal(r.executed_at)}</dd>
            </>
          ) : null}
          {r.outcome_reason && r.outcome_reason !== "canceled" ? (
            <>
              <dt>Outcome</dt>
              <dd>{humanize(r.outcome_reason)}</dd>
            </>
          ) : null}
        </dl>
        {r.single_user_fallback ? (
          <Banner tone="info" title="Extended verification instead of a second approver">
            {SINGLE_USER_FALLBACK_TEXT}
            {r.notified_at ? ` The notice was sent ${formatLocal(r.notified_at)}.` : " The notice hasn't been sent yet, so it can't take effect until it is."}
          </Banner>
        ) : null}
        {r.status === "pending" && !r.single_user_fallback && !r.can_approve && r.approval_blocker ? (
          <p className="small muted">You can't approve this: {blockerText(r.approval_blocker)}</p>
        ) : null}
        {approve.isSuccess ? (
          <Banner tone="success" live="polite">
            Approved. It takes effect no earlier than {formatLocal(approve.data.effective_at)}.
          </Banner>
        ) : null}
        {cancel.isSuccess ? (
          <Banner tone="success" live="polite">
            Canceled.
          </Banner>
        ) : null}
        {approve.isError ? (
          <CommandError error={approve.error}>
            <RejectionDetail error={approve.error} home={home} />
          </CommandError>
        ) : null}
        {cancel.isError ? (
          <CommandError error={cancel.error}>
            <RejectionDetail error={cancel.error} home={home} />
          </CommandError>
        ) : null}
        {(r.can_approve || r.can_cancel) && !approve.isSuccess && !cancel.isSuccess ? (
          <div className="row">
            {r.can_approve ? (
              <Button onClick={() => setConfirm("approve")} aria-label={`Approve: ${r.summary.text}`}>
                Approve
              </Button>
            ) : null}
            {r.can_cancel ? (
              <Button variant="secondary" onClick={() => setConfirm("cancel")} aria-label={`Cancel request: ${r.summary.text}`}>
                Cancel request
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <Dialog
        open={confirm === "approve"}
        onClose={() => setConfirm(null)}
        dismissible={!approve.isPending}
        title={`Approve: ${r.summary.text}?`}
        description={`You're the second approver. You'll confirm it's you first. It takes effect no earlier than ${formatLocal(r.effective_at)}, and either member can still cancel until then.`}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={approve.isPending}>
              Back
            </Button>
            <Button onClick={() => approve.mutate()} loading={approve.isPending}>
              Approve
            </Button>
          </>
        }
      />
      <Dialog
        open={confirm === "cancel"}
        alert
        onClose={() => setConfirm(null)}
        dismissible={!cancel.isPending}
        title={`Cancel: ${r.summary.text}?`}
        description="The change won't happen. Anyone who wants it will have to request it again."
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={cancel.isPending}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => cancel.mutate()} loading={cancel.isPending}>
              Cancel request
            </Button>
          </>
        }
      />
    </Card>
  );
}
